'use client';

import { Component, useEffect, useState, type ReactNode } from 'react';
import { BuiltinActionType, Renderer, type ActionEvent } from '@openuidev/react-lang';
import { ThemeProvider } from '@openuidev/react-ui';
import { ArrowsIn, ArrowsOut, ShareNetwork, Check, Copy, Eye, SpinnerGap, WarningCircle, X } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { galexiaDashboardLibrary } from '@/lib/genui-dashboard-library';
import { resolveTheme, type PersistedTheme } from '@/lib/openui-theme';

// Lab dashboard artifact panel. Pulled out of client.tsx so /labs/genui/[id]'s
// lab-chat-view can share the same right-side panel implementation when the
// model fires create_dashboard / update_dashboard / show_dashboard.
//
// Self-contained: ships its own RendererBoundary because RendererBoundary in
// the parent files is private to those modules.

export interface DashboardRecord {
  id: string;
  name: string;
  spec: string;
  theme: PersistedTheme | null;
  isPublic?: boolean;
}

class RendererBoundary extends Component<
  { fallbackText: string; children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error('[DashboardPanel] OpenUI Lang Renderer crashed:', error);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] px-4 py-3">
          <p className="text-sm font-medium text-amber-200/95">
            Dashboard render failed
          </p>
          <p className="mt-1 text-xs text-amber-200/70">
            {this.state.error.message}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-amber-200/55 hover:text-amber-200/85">
              Show raw spec
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-black/30 p-2 text-[10px] text-amber-200/65 whitespace-pre-wrap break-words font-mono">
              {this.props.fallbackText}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

export interface StreamingDashboard {
  spec: string;
  name: string;
  theme: PersistedTheme | null;
  /** True while the model is still streaming the spec; false once the
   *  tool input is fully assembled but the server-side row hasn't yet
   *  been created (output-available pending). The Renderer's isStreaming
   *  prop mirrors this so partial DSL renders progressively. */
  isStreaming: boolean;
}

export function DashboardPanel({
  dashboardId,
  refreshKey = 0,
  streamingDashboard,
  adminView = false,
  onClose,
  onSendInChat,
}: {
  dashboardId: string | null;
  /** When true, fetch via /api/admin/dashboards/[id] instead of the
   *  user-scoped /api/labs/dashboards/[id]. Lets admins view dashboards
   *  embedded in another user's chat without 404'ing on the user-id
   *  filter. requireAdmin() gates the admin endpoint. */
  adminView?: boolean;
  /**
   * Bump this to force a refetch when the dashboard's server-side spec has
   * changed but the id hasn't (typical after update_dashboard). The fetch
   * effect lists [dashboardId, refreshKey] in its deps so a bumped key
   * re-runs the fetch and re-renders the Renderer with the new spec.
   */
  refreshKey?: number;
  /**
   * Optional in-flight dashboard. When set, the panel renders the partial
   * spec via the OpenUI Lang Renderer (with isStreaming) instead of
   * fetching a saved row. Lets users watch the dashboard build itself in
   * real time as the model emits the spec, before the server-side row
   * exists. Once the tool completes, parent clears this and sets
   * dashboardId to switch to fetched mode.
   */
  streamingDashboard?: StreamingDashboard | null;
  onClose: () => void;
  onSendInChat: (text: string) => void;
}) {
  const [data, setData] = useState<DashboardRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // Share state. The button on the panel header opens a confirmation
  // dialog instead of toggling instantly — sharing is a one-click action
  // with privacy implications, so the user should see WHAT they're about
  // to make public before it goes public.
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareWorking, setShareWorking] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [justCopied, setJustCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  // Fetch the spec when the active id changes. Bail if id clears.
  useEffect(() => {
    if (!dashboardId) {
      setData(null);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetch(adminView ? `/api/admin/dashboards/${dashboardId}` : `/api/labs/dashboards/${dashboardId}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<DashboardRecord>;
      })
      .then((row) => {
        if (cancelled) return;
        setData(row);
        setIsPublic(!!row.isPublic);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load dashboard');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardId, refreshKey, adminView]);

  // ESC closes the panel — natural shortcut, doesn't conflict with the chat
  // input because that lives in the main column.
  useEffect(() => {
    if (!dashboardId && !streamingDashboard) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dashboardId, streamingDashboard, onClose]);

  if (!dashboardId && !streamingDashboard) return <div className="h-full" />;

  const handleAction = (event: ActionEvent) => {
    if (event.type === BuiltinActionType.ContinueConversation && event.humanFriendlyMessage) {
      onSendInChat(event.humanFriendlyMessage);
    }
  };

  const shareUrl = dashboardId ? `${typeof window !== 'undefined' ? window.location.origin : ''}/d/${dashboardId}` : '';

  // Confirm sharing — flips isPublic=true server-side and auto-copies the
  // URL on success. Called from the dialog's Confirm button when the
  // dashboard isn't yet public. No-op if already public.
  const handleConfirmShare = async () => {
    if (!dashboardId || isPublic) return;
    setShareWorking(true);
    setShareError(null);
    try {
      const res = await fetch(`/api/labs/dashboards/${dashboardId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setShareError(data.error || `Failed to share (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as { isPublic: boolean };
      setIsPublic(body.isPublic);
      // Auto-copy the URL the moment sharing flips on. Failures are silent
      // (clipboard permission may be denied); the URL field below stays
      // visible so the user can copy manually.
      if (body.isPublic) {
        try {
          await navigator.clipboard.writeText(`${window.location.origin}/d/${dashboardId}`);
          setJustCopied(true);
          setTimeout(() => setJustCopied(false), 2000);
        } catch {
          setCopyError('Auto-copy blocked. Click Copy to copy the link manually.');
        }
      }
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setShareWorking(false);
    }
  };

  // Revoke sharing — flips isPublic=false. Called from the dialog when
  // already public.
  const handleRevokeShare = async () => {
    if (!dashboardId || !isPublic) return;
    setShareWorking(true);
    setShareError(null);
    try {
      const res = await fetch(`/api/labs/dashboards/${dashboardId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: false }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setShareError(data.error || `Failed to revoke (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as { isPublic: boolean };
      setIsPublic(body.isPublic);
      setJustCopied(false);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setShareWorking(false);
    }
  };

  // Manual copy from the URL field in the dialog — re-tries the clipboard
  // path that may have been blocked at first auto-copy.
  const handleCopyUrl = async () => {
    if (!shareUrl) return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      setCopyError('Copy blocked — select the URL above and copy manually.');
    }
  };

  // Streaming preview takes priority — show the partial spec live as the
  // model writes it. Once the tool completes, parent will clear
  // streamingDashboard and set dashboardId, switching to fetched mode.
  const liveSpec = streamingDashboard?.spec ?? data?.spec ?? '';
  const liveName = streamingDashboard?.name ?? data?.name ?? (loading ? 'Loading…' : '');
  const liveTheme = streamingDashboard?.theme ?? data?.theme ?? null;
  const liveIsStreaming = streamingDashboard?.isStreaming ?? false;

  return (
    <aside
      role="dialog"
      aria-label={liveName || 'Artifact'}
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-background border-l border-white/[0.04]'
          : 'flex h-full flex-col border-l border-white/[0.04]'
      }
    >
      <header className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-white/[0.04]">
        {/* No "DASHBOARD" eyebrow / chart icon — the artifact may just be
            a free-form canvas of visuals (not a literal dashboard), so we
            let the LLM-generated name speak for itself. */}
        <p className="flex-1 min-w-0 truncate text-sm font-medium text-foreground/95">
          {liveName}
          {liveIsStreaming && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-muted-foreground/70">
              <span className="size-1.5 rounded-full bg-violet-400 animate-pulse" />
              building…
            </span>
          )}
        </p>
        {dashboardId && !streamingDashboard && (
          <button
            type="button"
            onClick={() => {
              setShareError(null);
              setCopyError(null);
              setShareDialogOpen(true);
            }}
            className={
              isPublic
                ? 'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium border border-violet-400/30 bg-violet-500/[0.08] text-violet-200 hover:border-violet-400/50 hover:bg-violet-500/[0.12] transition-colors'
                : 'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors'
            }
            aria-label={isPublic ? 'Manage public share' : 'Share dashboard'}
            title={isPublic ? 'Manage public share' : 'Share publicly'}
          >
            {isPublic ? (
              <>
                <ShareNetwork weight="bold" className="size-3.5" />
                Public
              </>
            ) : (
              <ShareNetwork weight="bold" className="size-4" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={() => setFullscreen((f) => !f)}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {fullscreen ? <ArrowsIn weight="bold" className="size-4" /> : <ArrowsOut weight="bold" className="size-4" />}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          aria-label="Close dashboard"
          title="Close (Esc)"
        >
          <X weight="bold" className="size-4" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8">
        {loadError && !streamingDashboard && (
          <div className="rounded-xl border border-red-500/25 bg-red-500/[0.05] px-4 py-3 text-sm text-red-200/95">
            Couldn&apos;t load this dashboard: {loadError}
          </div>
        )}
        {!loadError && liveSpec && (() => {
          const resolved = resolveTheme(liveTheme);
          const scopeId = streamingDashboard ? 'streaming' : (data?.id?.slice(0, 8) ?? 'panel');
          const scopeClass = `galexia-genui-dashboard-${scopeId}`;
          return (
            <div className={`${scopeClass} genui-prose`}>
              <ThemeProvider
                mode={resolved.mode}
                lightTheme={resolved.lightTheme}
                darkTheme={resolved.darkTheme}
                cssSelector={`.${scopeClass}`}
              >
                <RendererBoundary fallbackText={liveSpec}>
                  <Renderer
                    response={liveSpec}
                    library={galexiaDashboardLibrary}
                    isStreaming={liveIsStreaming}
                    onAction={handleAction}
                  />
                </RendererBoundary>
              </ThemeProvider>
            </div>
          );
        })()}
        {!loadError && !data && loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground/70">
            <span className="size-1.5 rounded-full bg-violet-400 animate-pulse" />
            Loading dashboard…
          </div>
        )}
      </div>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle
              className="font-normal"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, letterSpacing: -0.3 }}
            >
              {isPublic ? 'This dashboard is ' : 'Share this '}
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #6B8AFF, #A78BFA, #38BDF8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {isPublic ? 'public.' : 'dashboard.'}
              </span>
            </DialogTitle>
            <DialogDescription>
              {isPublic
                ? 'Anyone with the link can view this dashboard. They will see the title, charts, tables, and any embedded data — but not your chat history or other dashboards.'
                : 'Anyone with the link will be able to view this dashboard publicly. They will see the title, charts, tables, and any embedded data — but not your chat history or other dashboards. You can revoke the link any time.'}
            </DialogDescription>
          </DialogHeader>

          {shareError && (
            <div
              className="rounded-xl border border-destructive/20 bg-destructive/[0.04] px-4 py-3 flex items-start gap-2.5"
              role="alert"
            >
              <WarningCircle weight="duotone" className="size-4 shrink-0 mt-0.5 text-destructive" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive/95">Couldn&apos;t update share</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">{shareError}</p>
              </div>
            </div>
          )}

          {isPublic && (
            <div className="space-y-3">
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-sm font-mono text-foreground/90 outline-none focus:border-violet-500/40 focus:bg-white/[0.05] transition-colors"
                  aria-label="Public share URL"
                />
                <Button
                  size="sm"
                  onClick={handleCopyUrl}
                  aria-label={justCopied ? 'Copied to clipboard' : 'Copy share link'}
                  className={`shrink-0 gap-1.5 transition-all ${
                    justCopied
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20'
                      : 'bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white hover:scale-[1.02]'
                  }`}
                >
                  {justCopied ? (
                    <>
                      <Check weight="bold" className="size-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy weight="bold" className="size-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <div className="sr-only" role="status" aria-live="polite">
                {justCopied ? 'Share link copied to clipboard.' : ''}
              </div>
              {copyError && (
                <p
                  className="flex items-start gap-1.5 text-[12px] text-amber-300/85"
                  role="alert"
                  aria-live="assertive"
                >
                  <WarningCircle weight="duotone" className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{copyError}</span>
                </p>
              )}
              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground/55">
                <Eye weight="duotone" className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                <span>You can revoke the link anytime by clicking &ldquo;Make private&rdquo; below.</span>
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {isPublic ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setShareDialogOpen(false)}
                  className="text-muted-foreground"
                >
                  Done
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRevokeShare}
                  disabled={shareWorking}
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  {shareWorking ? (
                    <>
                      <SpinnerGap className="size-4 animate-spin" aria-hidden="true" />
                      Working…
                    </>
                  ) : (
                    'Make private'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setShareDialogOpen(false)}
                  className="text-muted-foreground"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmShare}
                  disabled={shareWorking}
                  className="bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white hover:scale-[1.02] gap-1.5"
                >
                  {shareWorking ? (
                    <>
                      <SpinnerGap className="size-4 animate-spin" aria-hidden="true" />
                      Sharing…
                    </>
                  ) : (
                    <>
                      <ShareNetwork weight="bold" className="size-4" aria-hidden="true" />
                      Share publicly
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
